<template>
  <div
    class="irp-recommended-partners-table"
    data-testid="irp-recommended-partners-table"
  >
    <h2 class="irp-recommended-partners-table__title">Recommended Partners</h2>
    <IRPRecommendedPartnersTableSkeleton v-if="isInitialLoading" />
    <BittsEmptyState
      v-else-if="hasError"
      :font-awesome-icon="ERROR_STATE.icon"
      :title="ERROR_STATE.title"
      :description="ERROR_STATE.description"
      :button="RETRY_BUTTON"
      data-testid="irp-recommended-partners-error-state"
      @button-clicked="refetch"
    />
    <BittsEmptyState
      v-else-if="!partners.length"
      :font-awesome-icon="EMPTY_STATE.icon"
      :title="EMPTY_STATE.title"
      :description="EMPTY_STATE.description"
      data-testid="irp-recommended-partners-empty-state"
    />
    <template v-else>
      <BittsTablePro
        :rows="partners"
        :columns="columnDefinitions"
        :default-column-def="DEFAULT_COLUMN_DEF"
        :row-height="56"
        :zebra="true"
        :pagination="false"
        :suppress-movable-columns="true"
      />
      <BittsPaginator
        v-if="hasMultiplePages"
        class="justify-end"
        :page="pagination.page"
        :last-page="lastPage"
        @page-click="setPage"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  BittsEmptyState,
  BittsPaginator,
  BittsTablePro,
} from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import type { ColDef } from '@ag-grid-community/core';
import { computed } from 'vue';

import { defaultErrorMessage } from '@/errors';
import { useIndividualRecordStore } from '@/stores';

import IRPRecommendedPartnersTableSkeleton from './IRPRecommendedPartnersTableSkeleton.vue';
import { useRecommendedPartnersColumnDefinitions } from './useRecommendedPartnersColumnDefinitions';

const DEFAULT_COLUMN_DEF: ColDef = { sortable: false, resizable: false };

const EMPTY_STATE: { title: string; description: string; icon: BittsIcon } = {
  title: 'No recommended partners',
  description: 'No partner has shared an overlapping record for this account.',
  icon: ['fak', 'partner'],
};

const ERROR_STATE: { title: string; description: string; icon: BittsIcon } = {
  title: 'Recommended partners could not load',
  description: defaultErrorMessage,
  icon: ['fak', 'warning'],
};

const RETRY_BUTTON = {
  text: 'Try again',
  type: 'neutral',
  variant: 'outline',
} as const;

const individualRecordStore = useIndividualRecordStore();

const columnDefinitions = useRecommendedPartnersColumnDefinitions();

const section = computed(() => individualRecordStore.all);
const partners = computed(() => section.value.partners.items);
const pagination = computed(() => section.value.partners.pagination);
const lastPage = computed(() => pagination.value.last_page ?? 1);
const hasMultiplePages = computed(() => lastPage.value > 1);
const isInitialLoading = computed(
  () => section.value.isLoading && !section.value.hasLoaded,
);

const hasError = computed(() => !!section.value.error);

function setPage(page: number) {
  return individualRecordStore.setPage('all', page);
}

async function refetch() {
  await individualRecordStore.fetchSection('all', { isForced: true });
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-recommended-partners-table {
  @apply flex flex-col gap-16;
}

.irp-recommended-partners-table__title {
  @apply text-lg font-bold text-neutral-text-strong;
}
</style>
